import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { Channel } from '@src/api/shared/community/channelApiClient';
import {
  Chat,
  chatApiClient,
  ChatReportErrorResponse,
  ChatReportRequest,
  ChatReportResponse,
  ThreadChat,
} from '@src/api/shared/community/chatApiClient';
import { ChatReportReasonEnum } from '@src/features/shared/community/enums/chatReportReason';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import logger from '@src/utils/logger';
import { useCallback, useState } from 'react';

type ChatReportParams = {
  channel: Channel;
  toast: (message: string) => void;
};

type ChatReportReturn = {
  reportingChatId: number | null;
  isReportModalOpen: boolean;
  handleChatReportClick: (chat: Chat | ThreadChat) => void;
  handleReportSubmit: (reason: ChatReportReasonEnum) => Promise<void>;
  handleReportCancel: () => void;
};

export const useChatReport = ({ channel, toast }: ChatReportParams): ChatReportReturn => {
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportingChatId, setReportingChatId] = useState<number | null>(null);

  const handleChatReportClick = useCallback((chat: Chat | ThreadChat) => {
    setReportingChatId(chat.id);
    setIsReportModalOpen(true);
  }, []);

  const handleReportSubmit = async (reason: ChatReportReasonEnum) => {
    if (!reportingChatId) {
      return;
    }

    const params: ChatReportRequest = { reason };
    const response = await chatApiClient.report(channel.code, reportingChatId, params);

    handleApiResponse<ChatReportResponse, ChatReportErrorResponse>(
      response,
      () => {
        setIsReportModalOpen(false);
        setReportingChatId(null);
        toast(TOAST_MESSAGE.SEND);
      },
      (errors) => {
        logger.error('Chat report failed:', errors);
      },
    );
  };

  const handleReportCancel = () => {
    setIsReportModalOpen(false);
    setReportingChatId(null);
  };

  return {
    reportingChatId,
    isReportModalOpen,
    handleChatReportClick,
    handleReportSubmit,
    handleReportCancel,
  };
};
